import {
  Box,
  ChartIcon,
  ClickAwayListener,
  Divider,
  DownIcon,
  Fade,
  FormControlLabel,
  GKButton,
  GKDatePicker,
  GKRadio,
  GKSearchSelect,
  GKTextField,
  Grid2,
  Popper,
  RadioGroup,
  RightIcon,
  Show,
  Typography,
} from "components";
import dayjs from "dayjs";
import { useState } from "react";
import {
  Controller,
  ControllerRenderProps,
  useFormContext,
} from "react-hook-form";
import { useLazyGetPortfolioAllocationQuery } from "store/features/MFOrders";
import { useAppSelector } from "store/store";
import type { MFOrderFormValues } from "./useMFOrders";
import { useWatchFields } from "./useMFOrders";

const FREQUENCY_OPTIONS = [
  { name: "Monthly", value: "monthly" },
  { name: "Quarterly", value: "quarterly" },
  { name: "Half Yearly", value: "half_yearly" },
  { name: "Yearly", value: "yearly" },
];

const FREQUENCY_MONTHS: Record<string, number> = {
  monthly: 1,
  quarterly: 3,
  half_yearly: 6,
  yearly: 12,
};

const DAY_LABEL: Record<string, string> = {
  monthly: "Day of Month",
  quarterly: "Day of Every Quarter",
  half_yearly: "Day of Every Half Year",
  yearly: "Day of Every Year",
};

const formatDuration = (
  frequency: string | null,
  installments: number | null,
): string => {
  if (!frequency || !installments) return "";
  const months = installments * (FREQUENCY_MONTHS[frequency] || 0);
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} Year${years > 1 ? "s" : ""}`);
  if (rem > 0) parts.push(`${rem} Month${rem > 1 ? "s" : ""}`);
  return parts.join(" ") || `${months} Months`;
};

type RightVisibleTableField = ControllerRenderProps<
  MFOrderFormValues,
  "extraThings.rightVisibleTable"
>;

interface OptionGridPickerProps {
  value: number | null;
  onChange: (v: number) => void;
  label: string;
  options: Array<{ name: string; value: number }>;
  columns: number;
  placeholder?: string;
}

const OptionGridPicker = ({
  value,
  onChange,
  label,
  options,
  columns,
  placeholder = "Select",
}: OptionGridPickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const open = Boolean(anchorEl);

  const selectedOption = options.find((o) => o.value === value);

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setAnchorEl(open ? null : e.currentTarget);
  };

  const handleSelect = (e: React.MouseEvent<HTMLDivElement>, next: number) => {
    e.stopPropagation();
    onChange(next);
    setAnchorEl(null);
  };

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <Typography
        color="var(--grey-900)"
        fontSize={13}
        fontWeight={500}
        sx={{ mb: "8px" }}
      >
        {label}
        <Typography component="span" color="var(--warning-700)" fontSize={13}>
          {" *"}
        </Typography>
      </Typography>
      <Box
        role="button"
        tabIndex={0}
        onClick={handleClick}
        sx={{
          alignItems: "center",
          bgcolor: "var(--white)",
          border: "1px solid var(--neutral-100)",
          borderRadius: "8px",
          cursor: "pointer",
          display: "flex",
          gap: "8px",
          height: 45,
          justifyContent: "space-between",
          p: "12px",
          width: "100%",
        }}
      >
        <Typography
          color={selectedOption ? "var(--black-900)" : "var(--neutral-500)"}
          fontSize={14}
          fontWeight={500}
        >
          {selectedOption ? selectedOption.name : placeholder}
        </Typography>
        <DownIcon height={16} width={16} />
      </Box>
      {open && (
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <Popper
            anchorEl={anchorEl}
            open={open}
            placement="bottom-start"
            disablePortal
            transition
            sx={{ zIndex: 1500, width: anchorEl?.offsetWidth }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={150}>
                <Box
                  sx={{
                    bgcolor: "var(--white)",
                    border: "1px solid var(--neutral-100)",
                    borderRadius: "8px",
                    boxShadow: "4px 4px 12px rgba(0, 0, 0, 0.15)",
                    mt: "4px",
                    p: "16px",
                    width: "100%",
                  }}
                >
                  <Typography
                    color="var(--black-900)"
                    fontSize={14}
                    fontWeight={600}
                    sx={{ mb: "12px", textAlign: "center" }}
                  >
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      display: "grid",
                      gap: "4px",
                      gridTemplateColumns: `repeat(${columns}, 1fr)`,
                    }}
                  >
                    {options.map((opt) => {
                      const selected = opt.value === value;
                      return (
                        <Box
                          key={opt.value}
                          role="button"
                          tabIndex={0}
                          onClick={(e) => handleSelect(e, opt.value)}
                          sx={{
                            alignItems: "center",
                            bgcolor: selected
                              ? "var(--primary-900)"
                              : "transparent",
                            borderRadius: "6px",
                            color: selected
                              ? "var(--white)"
                              : "var(--black-900)",
                            cursor: "pointer",
                            display: "flex",
                            fontSize: 14,
                            fontWeight: 500,
                            height: 36,
                            justifyContent: "center",
                            "&:hover": {
                              bgcolor: selected
                                ? "var(--primary-900)"
                                : "var(--neutral-50)",
                            },
                          }}
                        >
                          {opt.name}
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Fade>
            )}
          </Popper>
        </ClickAwayListener>
      )}
    </Box>
  );
};

const DAY_OF_MONTH_GRID = Array.from({ length: 28 }, (_, i) => ({
  name: String(i + 1),
  value: i + 1,
}));

const FormInputs = () => {
  const companies = useAppSelector((state) => state.app.companies);

  const form = useFormContext<MFOrderFormValues>();

  const {
    transactionField: watchTransactionType,
    orderType: watchOrderType,
    switchInGcode: watchSwitchInGcode,
    schemeName: watchSchemeName,
    isPerpetual: watchIsPerpetual,
    frequency: watchFrequency,
    installments: watchInstallments,
  } = useWatchFields();

  const planConfig = (() => {
    if (watchOrderType === "redemption") {
      return { label: "Plan (SWP)", value: "swp" as const, short: "SWP" };
    }
    if (watchOrderType === "switch") {
      return { label: "Plan (STP)", value: "stp" as const, short: "STP" };
    }
    return { label: "Plan (SIP)", value: "sip" as const, short: "SIP" };
  })();

  const isPlanSelected =
    watchTransactionType === "sip" ||
    watchTransactionType === "swp" ||
    watchTransactionType === "stp";

  const schemeReady =
    !!watchSchemeName && (watchOrderType !== "switch" || !!watchSwitchInGcode);

  const planShort: "SIP" | "SWP" | "STP" =
    watchTransactionType === "swp"
      ? "SWP"
      : watchTransactionType === "stp"
        ? "STP"
        : "SIP";

  const dayLabel = watchFrequency
    ? DAY_LABEL[watchFrequency] || "Day"
    : "Day of Month";

  const installmentsHelper = watchIsPerpetual
    ? `${planShort} runs until cancelled manually`
    : formatDuration(watchFrequency, watchInstallments);

  const [fetchAllocation] = useLazyGetPortfolioAllocationQuery();

  const handleSelectPortfolio = (field: RightVisibleTableField) => {
    field.onChange("SELECT_PORTFOLIO");
    const gcode = form.getValues("gcode");
    const orderType = form.getValues("orderType");
    if (
      gcode &&
      (orderType === "purchase" ||
        orderType === "redemption" ||
        orderType === "switch")
    ) {
      fetchAllocation({ gcode, orderType });
    }
  };

  return (
    <Grid2 container size={12} spacing={3}>
      <Grid2 size={12}>
        <Typography fontSize={13} fontWeight={600} color="var(--black-900)">
          Order Type <span style={{ color: "red" }}> *</span>
        </Typography>
        <Controller
          control={form.control}
          name="orderType"
          render={({ field }) => (
            <RadioGroup
              {...field}
              row
              onChange={(e) => {
                field.onChange(e.target.value);
                form.setValue("transactionType", "");
                if (e.target.value !== "switch") {
                  form.setValue("switchInGcode", null);
                }
                if (e.target.value === "redemption") {
                  form.setValue("transactionType", "lumpsum");
                }
              }}
              sx={{ gap: 3, marginTop: 1 }}
            >
              <FormControlLabel
                label="Purchase"
                value="purchase"
                sx={{ display: "flex", gap: 1, marginRight: 0 }}
                control={<GKRadio sx={{ padding: 0 }} />}
              />
              <FormControlLabel
                label="Redemption"
                value="redemption"
                sx={{ display: "flex", gap: 1, marginRight: 0 }}
                control={<GKRadio sx={{ padding: 0 }} />}
              />
              {/* <FormControlLabel
                label="Switch"
                value="switch"
                sx={{ display: "flex", gap: 1, marginRight: 0 }}
                control={<GKRadio sx={{ padding: 0 }} />}
              /> */}
            </RadioGroup>
          )}
        />
      </Grid2>

      <Show.When isTrue={!!watchOrderType}>
        <Grid2 size={12}>
          <Typography fontSize={13} fontWeight={600} color="var(--black-900)">
            Transaction Type <span style={{ color: "red" }}> *</span>
          </Typography>
          <Controller
            control={form.control}
            name="transactionType"
            render={({ field }) => (
              <RadioGroup
                {...field}
                row
                onChange={(e) => field.onChange(e.target.value)}
                sx={{ gap: 3, marginTop: 1 }}
              >
                <FormControlLabel
                  label="Lumpsum"
                  value="lumpsum"
                  sx={{ display: "flex", gap: 1, marginRight: 0 }}
                  control={<GKRadio sx={{ padding: 0 }} />}
                />
                {watchOrderType !== "redemption" && (
                  <FormControlLabel
                    label={planConfig.label}
                    value={planConfig.value}
                    sx={{ display: "flex", gap: 1, marginRight: 0 }}
                    control={<GKRadio sx={{ padding: 0 }} />}
                  />
                )}
              </RadioGroup>
            )}
          />
        </Grid2>

        <Grid2 size={12}>
          <Controller
            control={form.control}
            name="gcode"
            rules={{
              required: { value: true, message: "Scheme name require" },
            }}
            render={({ field, fieldState: { error } }) => (
              <GKSearchSelect
                {...field}
                requiredField
                disableClearable={false}
                inputLabel={
                  watchOrderType === "switch"
                    ? "Switch Out Scheme"
                    : "Scheme Name"
                }
                options={
                  companies?.filter((company) => company?.instType === "MF") ||
                  []
                }
                getOptionLabel={(option) => option?.name || option}
                onChange={(e, val) => {
                  const nextGcode = val?.value ?? "";
                  const prevGcode = field.value;
                  field.onChange(nextGcode);
                  if (nextGcode && nextGcode !== prevGcode) {
                    form.setValue("items", []);
                    const orderType = form.getValues("orderType");
                    if (
                      orderType === "purchase" ||
                      orderType === "redemption" ||
                      orderType === "switch"
                    ) {
                      fetchAllocation({ gcode: nextGcode, orderType });
                    }
                  }
                }}
                value={
                  companies?.find(
                    (dataItem: any) => dataItem.gridCode === field.value,
                  ) || ""
                }
                error={Boolean(error)}
                helperText={error?.message as string}
              />
            )}
          />
        </Grid2>

        <Show.When isTrue={watchOrderType === "switch"}>
          <Grid2 size={12}>
            <Controller
              control={form.control}
              name="switchInGcode"
              rules={{
                required: {
                  value: true,
                  message: "Switch in scheme required",
                },
              }}
              render={({ field, fieldState: { error } }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  disableClearable={false}
                  inputLabel="Switch In Scheme"
                  options={
                    companies?.filter(
                      (c: any) =>
                        c?.instType === "MF" && c?.gridCode !== watchSchemeName,
                    ) || []
                  }
                  getOptionLabel={(option) => option?.name || option}
                  onChange={(e, val) => field.onChange(val?.value ?? null)}
                  value={
                    companies?.find((c: any) => c.gridCode === field.value) ||
                    ""
                  }
                  error={Boolean(error)}
                  helperText={error?.message as string}
                />
              )}
            />
          </Grid2>
        </Show.When>
      </Show.When>

      <Show.When isTrue={schemeReady}>
        <Show.When isTrue={watchTransactionType === "lumpsum"}>
          <Grid2 size={12}>
            <Controller
              name="scheduledFor"
              control={form.control}
              render={({ field }) => (
                <GKDatePicker
                  {...field}
                  inputLabel={"Order Placed Date"}
                  value={dayjs(field.value)}
                  onChange={(value) => {
                    field.onChange(dayjs(value).format("YYYY-MM-DD"));
                  }}
                  disableFuture
                  slotProps={{
                    textField: {
                      error: false,
                      helperText: false,
                    },
                  }}
                />
              )}
            />
          </Grid2>
        </Show.When>

        <Show.When isTrue={isPlanSelected}>
          <Grid2 size={12}>
            <Divider />
          </Grid2>

          <Grid2 size={12}>
            <Controller
              control={form.control}
              name="frequency"
              render={({ field }) => (
                <GKSearchSelect
                  {...field}
                  requiredField
                  disableClearable
                  inputLabel={`${planShort} Frequency`}
                  options={FREQUENCY_OPTIONS}
                  getOptionLabel={(option) => option?.name || option}
                  onChange={(e, val) => field.onChange(val?.value ?? null)}
                  value={
                    FREQUENCY_OPTIONS.find((o) => o.value === field.value) || ""
                  }
                />
              )}
            />
          </Grid2>

          <Grid2 size={12}>
            {watchFrequency === "yearly" && (
              <Controller
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <GKDatePicker
                    {...field}
                    inputLabel={dayLabel}
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(value) => {
                      field.onChange(
                        value ? dayjs(value).format("YYYY-MM-DD") : null,
                      );
                    }}
                    format="D MMM"
                    slotProps={{
                      textField: { error: false, helperText: false },
                    }}
                  />
                )}
              />
            )}
            {(watchFrequency === "monthly" ||
              watchFrequency === "quarterly" ||
              watchFrequency === "half_yearly") && (
              <Controller
                control={form.control}
                name="dayOfMonth"
                render={({ field }) => (
                  <OptionGridPicker
                    label={dayLabel}
                    value={field.value}
                    onChange={(v) => field.onChange(v)}
                    options={DAY_OF_MONTH_GRID}
                    columns={6}
                  />
                )}
              />
            )}
          </Grid2>

          <Grid2 size={12}>
            <Typography
              fontSize={13}
              fontWeight={600}
              color="var(--black-900)"
              sx={{ mb: "8px" }}
            >
              No. of Installments
              <span style={{ color: "red" }}> *</span>
            </Typography>
            <Controller
              control={form.control}
              name="isPerpetual"
              render={({ field: perpetualField }) => (
                <RadioGroup
                  row
                  value={perpetualField.value ? "perpetual" : "custom"}
                  onChange={(e) => {
                    const isPerp = e.target.value === "perpetual";
                    perpetualField.onChange(isPerp);
                    if (isPerp) {
                      form.setValue("installments", null);
                    }
                  }}
                >
                  <FormControlLabel
                    label="Perpetual"
                    value="perpetual"
                    control={<GKRadio disableLeftPadding />}
                  />
                  <FormControlLabel
                    label="Custom"
                    value="custom"
                    control={<GKRadio disableLeftPadding />}
                  />
                </RadioGroup>
              )}
            />
            {!watchIsPerpetual && (
              <Box sx={{ mt: "12px" }}>
                <Controller
                  control={form.control}
                  name="installments"
                  render={({ field }) => (
                    <GKTextField
                      {...field}
                      placeholder="No. of Installments"
                      type="number"
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        field.onChange(v === "" ? null : Number(v));
                      }}
                      helperText={installmentsHelper}
                    />
                  )}
                />
              </Box>
            )}
            {watchIsPerpetual && (
              <Typography
                color="var(--neutral-500)"
                fontSize={12}
                sx={{ mt: "8px" }}
              >
                {installmentsHelper}
              </Typography>
            )}
          </Grid2>

          <Show.When isTrue={watchTransactionType === "stp"}>
            <Grid2 size={12}>
              <Controller
                name="startDate"
                control={form.control}
                render={({ field }) => (
                  <GKDatePicker
                    {...field}
                    inputLabel="Order Placed Date"
                    value={field.value ? dayjs(field.value) : null}
                    onChange={(value) => {
                      field.onChange(
                        value ? dayjs(value).format("YYYY-MM-DD") : null,
                      );
                    }}
                    slotProps={{
                      textField: { error: false, helperText: false },
                    }}
                  />
                )}
              />
            </Grid2>
          </Show.When>
        </Show.When>

        <Grid2 size={12}>
          <Divider />
        </Grid2>

        <Grid2 size={12}>
          <Controller
            control={form.control}
            name="extraThings.rightVisibleTable"
            render={({ field }) => (
              <GKButton
                fullWidth
                variant="contained"
                startIcon={<ChartIcon />}
                endIcon={<RightIcon />}
                onClick={() => handleSelectPortfolio(field)}
              >
                Select Portfolio
              </GKButton>
            )}
          />
        </Grid2>
      </Show.When>
    </Grid2>
  );
};

export default FormInputs;
